import { useCallback } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../config/queryKeys'
import { getScheduleLeaderboard } from '../services/competition/getScheduleLeaderboard'
import { resolveAssetUrl } from '../utils/assetUrl'

// Placeholder images
const PLAYER_PLACEHOLDER = '/images/placeholders/hockey/player.png'
const TEAM_PLACEHOLDER = '/images/placeholders/hockey/team.png'

/**
 * Shape of a single player in a leaderboard category
 */
export interface LeaderboardPlayer {
  id: string
  playerId: string
  rank: number
  name: string
  teamId: string
  teamName: string
  teamLogo: string
  playerImage: string
  statValue: number | string
}

/**
 * A leaderboard category (e.g., Goals, Assists, Points)
 */
export interface LeaderboardCategory {
  title: string
  statId: string
  abbreviation: string
  precision: number
  players: LeaderboardPlayer[]
}

/**
 * Transformed leaderboard data for components
 */
export interface LeaderboardData {
  players: LeaderboardCategory[]
  goalies: LeaderboardCategory[]
}

/**
 * Return type for useLeaderboard hook
 */
export interface UseLeaderboardReturn {
  data: LeaderboardData | null
  loading: boolean
  error: Error | null
  refetch: () => Promise<void>
}

/**
 * API response shape for a single leader entry
 */
interface ApiLeaderEntry {
  id?: string
  player_id?: string
  player?: {
    id?: string
    name_first?: string
    name_last?: string
    full_name?: string
    image?: { full_path?: string }
  }
  team?: {
    id?: string
    name_full?: string
    image?: { full_path?: string }
  }
  value?: number | string
  stat_value?: number | string
}

/**
 * API response shape for a category
 */
interface ApiCategory {
  name?: string
  title?: string
  abbreviation?: string
  stat_type_id?: number
  precision?: number
  leaders?: ApiLeaderEntry[]
  data?: ApiLeaderEntry[]
}

/**
 * Possible API response structures
 */
interface ApiLeaderboardResponse {
  player?: ApiCategory[] | Record<string, ApiLeaderEntry[]>
  goalie?: ApiCategory[] | Record<string, ApiLeaderEntry[]>
  data?: {
    player?: ApiCategory[] | Record<string, ApiLeaderEntry[]>
    goalie?: ApiCategory[] | Record<string, ApiLeaderEntry[]>
  }
}

/**
 * Get user-friendly error message based on error type
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const err = error as Error & {
      statusCode?: number
      status?: number
      isApiError?: boolean
    }
    const statusCode = err.statusCode ?? err.status

    if (statusCode === 404) {
      return 'Leaderboard Data Not Found For This Schedule.'
    }
    if (statusCode === 401 || statusCode === 403) {
      return "You Don't Have Permission To View This Leaderboard."
    }
    if (statusCode === 500 || statusCode === 502 || statusCode === 503) {
      return 'Server Error Occurred. Please Try Again Later.'
    }
    if (
      err.message?.includes('Failed to fetch') ||
      err.message?.includes('Network')
    ) {
      return 'Unable To Connect. Please Check Your Internet Connection.'
    }
    if (err.isApiError) {
      return err.message || 'Failed To Load Leaderboard Data. Please Try Again.'
    }
  }
  return 'Failed To Load Leaderboard Data. Please Try Again.'
}

/**
 * Transform API leader entry to component format
 */
function transformLeaderEntry(
  entry: ApiLeaderEntry,
  rank: number
): LeaderboardPlayer {
  const playerId = entry.player_id ?? entry.player?.id ?? entry.id ?? ''
  const playerName =
    entry.player?.full_name ??
    (entry.player?.name_first && entry.player?.name_last
      ? `${entry.player.name_first} ${entry.player.name_last}`
      : '')

  return {
    id: entry.id ?? `${playerId}-${rank}`,
    playerId,
    rank,
    name: playerName,
    teamId: entry.team?.id ?? '',
    teamName: entry.team?.name_full ?? '',
    teamLogo: resolveAssetUrl(entry.team?.image) ?? TEAM_PLACEHOLDER,
    playerImage: resolveAssetUrl(entry.player?.image) ?? PLAYER_PLACEHOLDER,
    statValue: entry.value ?? entry.stat_value ?? 0,
  }
}

/**
 * Transform API category to component format
 */
function transformCategory(
  categoryName: string,
  entries: ApiLeaderEntry[],
  precision: number = 0,
  statId: string = '',
  abbreviation: string = ''
): LeaderboardCategory {
  // Format title: convert snake_case to Title Case
  const title = categoryName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')

  return {
    title,
    statId,
    abbreviation,
    precision,
    players: entries.map((entry, index) =>
      transformLeaderEntry(entry, index + 1)
    ),
  }
}

/**
 * Transform API response to component-friendly format
 */
function transformApiResponse(
  response: ApiLeaderboardResponse
): LeaderboardData {
  const rawData = response.data ?? response
  const result: LeaderboardData = {
    players: [],
    goalies: [],
  }

  // Handle players section
  const playersData = rawData.player

  if (playersData) {
    if (Array.isArray(playersData)) {
      // Array of categories: [{ name: 'goals', leaders: [...] }, ...]
      result.players = playersData.map((cat) => {
        const entries = cat.leaders ?? cat.data ?? []
        const title = cat.title ?? cat.name ?? cat.abbreviation ?? 'Unknown'
        return transformCategory(
          title,
          entries,
          cat.precision ?? 0,
          cat.stat_type_id?.toString() ?? '',
          cat.abbreviation ?? ''
        )
      })
    } else if (typeof playersData === 'object') {
      // Object format: { goals: [...], assists: [...] }
      result.players = Object.entries(playersData).map(([key, entries]) =>
        transformCategory(key, entries as ApiLeaderEntry[], 0, '', key)
      )
    }
  }

  // Handle goalies section
  const goaliesData = rawData.goalie
  if (goaliesData) {
    if (Array.isArray(goaliesData)) {
      result.goalies = goaliesData.map((cat) => {
        const entries = cat.leaders ?? cat.data ?? []
        const title = cat.title ?? cat.name ?? cat.abbreviation ?? 'Unknown'
        return transformCategory(
          title,
          entries,
          cat.precision ?? 0,
          cat.stat_type_id?.toString() ?? '',
          cat.abbreviation ?? ''
        )
      })
    } else if (typeof goaliesData === 'object') {
      result.goalies = Object.entries(goaliesData).map(([key, entries]) =>
        transformCategory(key, entries as ApiLeaderEntry[], 0, '', key)
      )
    }
  }

  return result
}

/**
 * Check if leaderboard data is empty
 */
export function isLeaderboardEmpty(data: LeaderboardData | null): boolean {
  if (!data) {
    return true
  }

  const hasPlayers = data.players.some((cat) => cat.players.length > 0)
  const hasGoalies = data.goalies.some((cat) => cat.players.length > 0)

  return !hasPlayers && !hasGoalies
}

/**
 * Custom hook for fetching leaderboard data
 *
 * @param scheduleId - The schedule ID to fetch leaderboard data for
 * @returns Object containing data, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { data, loading, error, refetch } = useLeaderboard(scheduleId)
 *
 * if (loading) return <Spinner />
 * if (error) return <ErrorState message={error.message} onRetry={refetch} />
 * if (!data || isLeaderboardEmpty(data)) return <EmptyState />
 *
 * return <LeaderboardGrid data={data} />
 * ```
 */
export function useLeaderboard(
  scheduleId?: string,
  divisionId?: string,
  conferenceId?: string,
  global?: boolean,
  goalie?: boolean
): UseLeaderboardReturn & {
  /** True while `data` is still the previous filters' result. */
  isPreviousData: boolean
} {
  const query = useQuery({
    queryKey: queryKeys.scheduleLeaderboard.bySchedule(scheduleId, {
      divisionId,
      conferenceId,
      global,
      goalie,
    }),
    queryFn: async ({ signal }) => {
      try {
        return await getScheduleLeaderboard(
          scheduleId as string,
          { divisionId, conferenceId, global, goalie },
          signal
        )
      } catch (err) {
        // A friendly message for the UI, keeping the status so the shared
        // retry policy still skips 4xx responses.
        const original = err as { status?: number; statusCode?: number }
        throw Object.assign(new Error(getErrorMessage(err)), {
          status: original?.status,
          statusCode: original?.statusCode,
        })
      }
    },
    enabled: Boolean(scheduleId),
    // A filter change keeps the current board on screen until the new one
    // lands, as the hook always did.
    placeholderData: keepPreviousData,
    select: selectLeaderboard,
  })

  const { refetch } = query
  const refetchLeaderboard = useCallback(async (): Promise<void> => {
    await refetch()
  }, [refetch])

  return {
    data: scheduleId ? query.data ?? null : null,
    loading: query.isFetching,
    error: query.error,
    refetch: refetchLeaderboard,
    isPreviousData: query.isPlaceholderData,
  }
}

/** Module-level so React Query keeps the mapped board between renders. */
const selectLeaderboard = (response: unknown): LeaderboardData =>
  transformApiResponse(response as ApiLeaderboardResponse)

export default useLeaderboard
